import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Tv, Save, Send, Loader2, Upload, Trash2, ImageIcon, Clock, Hash,
  CheckCircle2, XCircle, Calendar, Zap, Image as ImgIcon, Quote, PartyPopper, History, PenLine, RotateCcw,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import ManualPostDialog from "./ManualPostDialog";

interface Settings {
  id: number;
  channel_chat_id: string | null;
  channel_username: string | null;
  send_hour: number;
  send_minute: number;
  timezone_offset_minutes: number;
  weekdays_only: boolean;
  post_days: number[];
  morning_gif_enabled: boolean;
  quote_image_enabled: boolean;
  holiday_enabled: boolean;
}

interface GifRow {
  id: string;
  storage_path: string;
  caption: string | null;
  is_active: boolean;
  last_used_at: string | null;
  signedUrl?: string;
}

interface LogRow {
  id: string;
  post_date: string;
  post_type: string;
  status: string;
  created_at: string;
  details: any;
}

const DEFAULTS: Settings = {
  id: 1,
  channel_chat_id: "",
  channel_username: "",
  send_hour: 7,
  send_minute: 0,
  timezone_offset_minutes: 180,
  weekdays_only: true,
  post_days: [1, 2, 3, 4, 5],
  morning_gif_enabled: true,
  quote_image_enabled: true,
  holiday_enabled: true,
};

const TZ_PRESETS = [
  { label: "UTC+3 Addis Ababa", value: 180 },
  { label: "UTC+0 London", value: 0 },
  { label: "UTC-5 New York", value: -300 },
  { label: "UTC+1 Berlin", value: 60 },
  { label: "UTC+4 Dubai", value: 240 },
];

function formatLocalTime(h: number, m: number, tzOffsetMin: number) {
  const hh = String(h).padStart(2, "0");
  const mm = String(m).padStart(2, "0");
  const sign = tzOffsetMin >= 0 ? "+" : "-";
  const absH = Math.floor(Math.abs(tzOffsetMin) / 60);
  const absM = Math.abs(tzOffsetMin) % 60;
  return `${hh}:${mm} (UTC${sign}${absH}${absM ? `:${String(absM).padStart(2, "0")}` : ""})`;
}

function nextRunCountdown(s: Settings): string {
  const now = new Date();
  const tzMs = s.timezone_offset_minutes * 60 * 1000;
  const localNow = new Date(now.getTime() + tzMs);
  const target = new Date(localNow);
  target.setUTCHours(s.send_hour, s.send_minute, 0, 0);
  if (target <= localNow) target.setUTCDate(target.getUTCDate() + 1);
  const allowedDays = s.post_days && s.post_days.length > 0
    ? s.post_days
    : (s.weekdays_only ? [1, 2, 3, 4, 5] : [0, 1, 2, 3, 4, 5, 6]);
  let guard = 0;
  while (!allowedDays.includes(target.getUTCDay()) && guard < 14) {
    target.setUTCDate(target.getUTCDate() + 1);
    guard++;
  }
  if (guard >= 14) return "never (no days selected)";
  const diff = target.getTime() - localNow.getTime();
  const hrs = Math.floor(diff / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  if (hrs >= 24) return `in ${Math.floor(hrs / 24)}d ${hrs % 24}h`;
  return `in ${hrs}h ${mins}m`;
}

const POST_TYPE_META: Record<string, { icon: any; label: string; color: string }> = {
  morning_gif: { icon: ImgIcon, label: "Morning GIF", color: "text-amber-500" },
  quote_image: { icon: Quote, label: "Daily Quote", color: "text-blue-500" },
  holiday: { icon: PartyPopper, label: "Holiday", color: "text-pink-500" },
  manual_telegram: { icon: PenLine, label: "Manual post", color: "text-primary" },
};

const ChannelSettingsCard = ({ sessionToken }: { sessionToken: string | null }) => {
  const adminCall = async (action: string, payload: Record<string, unknown> = {}) => {
    const { data, error } = await supabase.functions.invoke("admin-channel", {
      body: { action, sessionToken, ...payload },
    });
    if (error) throw new Error(error.message);
    if ((data as any)?.error) throw new Error((data as any).error);
    return data as any;
  };

  const [s, setS] = useState<Settings>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  const [gifs, setGifs] = useState<GifRow[]>([]);
  const [logs, setLogs] = useState<LogRow[]>([]);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [tick, setTick] = useState(0);
  const [manualOpen, setManualOpen] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const isConnected = !!(s.channel_chat_id?.trim() || s.channel_username?.trim());

  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  const countdown = useMemo(() => nextRunCountdown(s), [s, tick]);

  const loadSettings = async () => {
    const r = await adminCall("get_settings").catch(() => null);
    if (r?.settings) setS({ ...DEFAULTS, ...r.settings });
  };

  const loadGifs = async () => {
    const r = await adminCall("list_gifs").catch(() => null);
    setGifs(((r?.gifs ?? []) as GifRow[]));
  };

  const loadLogs = async () => {
    // Telegram-only history. Meta (FB/IG) posts live in MetaSocialCard.
    const r = await adminCall("list_logs", { scope: "telegram", limit: 20 }).catch(() => null);
    setLogs(((r?.logs ?? []) as LogRow[]));
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadSettings(), loadGifs(), loadLogs()]);
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await adminCall("save_settings", {
        patch: {
          ...s,
          channel_chat_id: s.channel_chat_id?.trim() || null,
          channel_username: s.channel_username?.replace(/^@/, "").trim() || null,
        },
      });
      toast({ title: "✅ Saved", description: "Channel settings updated." });
      loadSettings();
    } catch (e: any) {
      toast({ title: "Save failed", description: e.message, variant: "destructive" });
    }
    setSaving(false);
  };

  const [sendingType, setSendingType] = useState<string | null>(null);

  const sendNow = async (type?: "morning_gif" | "quote_image" | "holiday") => {
    if (!isConnected) {
      toast({ title: "Channel not set", description: "Add a chat ID or @username first.", variant: "destructive" });
      return;
    }
    setSendingNow(true);
    setSendingType(type ?? "all");
    try {
      const { data, error } = await supabase.functions.invoke("daily-channel-post", {
        body: { force: true, ...(type ? { type } : {}) },
      });
      if (error) throw error;
      const d = data as any;
      if (d?.ok === false) {
        toast({ title: "Skipped", description: d.reason || "Function declined to post.", variant: "destructive" });
      } else {
        const r = d?.results || {};
        const summary = Object.entries(r).map(([k, v]) => `${POST_TYPE_META[k]?.label || k}: ${v}`).join(" • ");
        toast({ title: "📨 Posted", description: summary || "Sent." });
        loadLogs();
        loadGifs();
      }
    } catch (e: any) {
      toast({ title: "Failed", description: e.message, variant: "destructive" });
    } finally {
      setSendingNow(false);
      setSendingType(null);
    }
  };

  // Retry a previously-failed Telegram post. Re-runs the same content type
  // (morning_gif / quote_image / holiday) which regenerates AI content + image
  // and re-sends to Telegram. May take 15-45s due to AI image generation.
  const retryPost = async (log: LogRow) => {
    const retryableTypes = ["morning_gif", "quote_image", "holiday"];
    if (!retryableTypes.includes(log.post_type)) {
      toast({ title: "Cannot retry", description: "This post type does not support retry from history.", variant: "destructive" });
      return;
    }
    if (!isConnected) {
      toast({ title: "Channel not set", description: "Add a chat ID or @username first.", variant: "destructive" });
      return;
    }
    setRetryingId(log.id);
    try {
      const { data, error } = await supabase.functions.invoke("daily-channel-post", {
        body: { force: true, type: log.post_type },
      });
      if (error) throw error;
      const d = data as any;
      if (d?.ok === false) {
        toast({ title: "Retry skipped", description: d.reason || "Function declined to post.", variant: "destructive" });
      } else {
        const r = d?.results || {};
        const status = r[log.post_type] || "sent";
        const ok = !/fail|error|not found|missing/i.test(String(status));
        toast({
          title: ok ? "✅ Retried" : "Retry failed",
          description: `${POST_TYPE_META[log.post_type]?.label || log.post_type}: ${status}`,
          variant: ok ? "default" : "destructive",
        });
        loadLogs();
      }
    } catch (e: any) {
      toast({ title: "Retry failed", description: e.message ?? "Unknown error", variant: "destructive" });
    } finally {
      setRetryingId(null);
    }
  };

  const uploadFile = async (file: File) => {
    if (!/\.(gif|png|jpg|jpeg|webp)$/i.test(file.name)) {
      toast({ title: "Invalid file", description: "Use .gif, .png, .jpg, or .webp", variant: "destructive" });
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast({ title: "Too large", description: "Max 20MB.", variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      const ext = file.name.split(".").pop() ?? "png";
      const { path, token } = await adminCall("upload_url", { ext });
      const { error: upErr } = await supabase.storage
        .from("channel-assets")
        .uploadToSignedUrl(path, token, file, { contentType: file.type });
      if (upErr) throw upErr;
      await adminCall("add_gif", { storage_path: path, caption: caption.trim() || null });
      toast({ title: "🎬 Uploaded", description: "Added to rotation." });
      setCaption("");
      if (fileRef.current) fileRef.current.value = "";
      loadGifs();
    } catch (err: any) {
      toast({ title: "Upload failed", description: err.message, variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  const toggleActive = async (g: GifRow) => {
    try {
      await adminCall("toggle_gif", { id: g.id, is_active: !g.is_active });
    } catch (e: any) {
      toast({ title: "Update failed", description: e.message, variant: "destructive" });
    }
    loadGifs();
  };

  const removeGif = async (g: GifRow) => {
    if (!(await confirm("Delete this GIF from rotation?"))) return;
    try {
      await adminCall("delete_gif", { id: g.id, storage_path: g.storage_path });
      toast({ title: "Deleted" });
    } catch (e: any) {
      toast({ title: "Delete failed", description: e.message, variant: "destructive" });
    }
    loadGifs();
  };

  if (loading) return <Skeleton className="h-96 w-full" />;

  const activeGifs = gifs.filter((g) => g.is_active).length;
  const enabledFeatures = [s.morning_gif_enabled, s.quote_image_enabled, s.holiday_enabled].filter(Boolean).length;

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3 border-b bg-gradient-to-br from-primary/5 to-transparent">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-base flex items-center gap-2">
            <Tv className="w-5 h-5 text-primary" /> Telegram Channel — Daily Posts
          </CardTitle>
          <div className="flex items-center gap-2">
            {isConnected ? (
              <Badge variant="outline" className="gap-1.5 border-green-500/30 bg-green-500/10 text-green-600 dark:text-green-400">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                Connected
              </Badge>
            ) : (
              <Badge variant="outline" className="gap-1.5 border-destructive/30 bg-destructive/10 text-destructive">
                <span className="w-1.5 h-1.5 rounded-full bg-destructive" />
                Not connected
              </Badge>
            )}
          </div>
        </div>

        {/* Status strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-3">
          <div className="rounded-lg border bg-card p-2.5">
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Channel</div>
            <div className="text-xs font-semibold truncate">
              {s.channel_username ? `@${s.channel_username.replace(/^@/, "")}` : s.channel_chat_id || "—"}
            </div>
          </div>
          <div className="rounded-lg border bg-card p-2.5">
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Next post</div>
            <div className="text-xs font-semibold flex items-center gap-1">
              <Clock className="w-3 h-3 text-primary" /> {isConnected ? countdown : "—"}
            </div>
          </div>
          <div className="rounded-lg border bg-card p-2.5">
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Active types</div>
            <div className="text-xs font-semibold">{enabledFeatures} / 3 enabled</div>
          </div>
          <div className="rounded-lg border bg-card p-2.5">
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">GIF library</div>
            <div className="text-xs font-semibold">{activeGifs} active / {gifs.length} total</div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <Tabs defaultValue="schedule" className="w-full">
          <TabsList className="w-full justify-start rounded-none border-b bg-muted/30 px-2 h-auto">
            <TabsTrigger value="schedule" className="gap-1.5 text-xs"><Clock className="w-3.5 h-3.5" /> Schedule</TabsTrigger>
            <TabsTrigger value="channel" className="gap-1.5 text-xs"><Hash className="w-3.5 h-3.5" /> Channel</TabsTrigger>
            <TabsTrigger value="content" className="gap-1.5 text-xs"><Zap className="w-3.5 h-3.5" /> Content</TabsTrigger>
            <TabsTrigger value="gifs" className="gap-1.5 text-xs"><ImageIcon className="w-3.5 h-3.5" /> GIFs ({gifs.length})</TabsTrigger>
            <TabsTrigger value="history" className="gap-1.5 text-xs"><History className="w-3.5 h-3.5" /> History</TabsTrigger>
          </TabsList>

          {/* SCHEDULE */}
          <TabsContent value="schedule" className="p-4 space-y-4 m-0">
            <div className="rounded-lg border p-4 bg-gradient-to-br from-primary/5 to-transparent">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Send time (channel local)</Label>
              <div className="flex items-center gap-3 mt-2">
                <Input
                  type="number" min={0} max={23}
                  value={s.send_hour}
                  onChange={(e) => setS({ ...s, send_hour: Math.max(0, Math.min(23, parseInt(e.target.value) || 0)) })}
                  className="w-20 text-center text-2xl font-bold h-14"
                />
                <span className="text-3xl font-bold text-muted-foreground">:</span>
                <Input
                  type="number" min={0} max={59}
                  value={s.send_minute}
                  onChange={(e) => setS({ ...s, send_minute: Math.max(0, Math.min(59, parseInt(e.target.value) || 0)) })}
                  className="w-20 text-center text-2xl font-bold h-14"
                />
                <div className="flex-1 text-xs text-muted-foreground pl-2">
                  <div className="font-medium">{formatLocalTime(s.send_hour, s.send_minute, s.timezone_offset_minutes)}</div>
                  <div className="opacity-70 mt-0.5">Next: {countdown}</div>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Timezone</Label>
              <div className="flex flex-wrap gap-1.5">
                {TZ_PRESETS.map((p) => (
                  <Button
                    key={p.value}
                    size="sm"
                    variant={s.timezone_offset_minutes === p.value ? "default" : "outline"}
                    onClick={() => setS({ ...s, timezone_offset_minutes: p.value })}
                    className="text-xs h-7"
                  >
                    {p.label}
                  </Button>
                ))}
              </div>
              <Input
                type="number"
                value={s.timezone_offset_minutes}
                onChange={(e) => setS({ ...s, timezone_offset_minutes: parseInt(e.target.value) || 0 })}
                placeholder="Custom offset (minutes from UTC)"
                className="text-xs"
              />
            </div>

            <div className="space-y-2 p-3 rounded-lg border bg-muted/30">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium flex items-center gap-1.5"><Calendar className="w-4 h-4" /> Post on these days</div>
                  <div className="text-[11px] text-muted-foreground">Tap to toggle each day. The post fires only on selected days.</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 pt-1">
                {[
                  { idx: 1, label: "Mon" },
                  { idx: 2, label: "Tue" },
                  { idx: 3, label: "Wed" },
                  { idx: 4, label: "Thu" },
                  { idx: 5, label: "Fri" },
                  { idx: 6, label: "Sat" },
                  { idx: 0, label: "Sun" },
                ].map((d) => {
                  const active = (s.post_days || []).includes(d.idx);
                  return (
                    <Button
                      key={d.idx}
                      type="button"
                      size="sm"
                      variant={active ? "default" : "outline"}
                      onClick={() => {
                        const cur = new Set(s.post_days || []);
                        if (active) cur.delete(d.idx); else cur.add(d.idx);
                        const next = Array.from(cur).sort((a, b) => a - b);
                        const isWeekdays = next.length === 5 && [1, 2, 3, 4, 5].every((x) => next.includes(x));
                        setS({ ...s, post_days: next, weekdays_only: isWeekdays });
                      }}
                      className="text-xs h-7 px-2.5 min-w-[42px]"
                    >
                      {d.label}
                    </Button>
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-1.5 pt-1">
                <Button type="button" size="sm" variant="ghost" className="text-[11px] h-6"
                  onClick={() => setS({ ...s, post_days: [1, 2, 3, 4, 5], weekdays_only: true })}>
                  Weekdays
                </Button>
                <Button type="button" size="sm" variant="ghost" className="text-[11px] h-6"
                  onClick={() => setS({ ...s, post_days: [0, 1, 2, 3, 4, 5, 6], weekdays_only: false })}>
                  Every day
                </Button>
                <Button type="button" size="sm" variant="ghost" className="text-[11px] h-6"
                  onClick={() => setS({ ...s, post_days: [0, 6], weekdays_only: false })}>
                  Weekends only
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* CHANNEL */}
          <TabsContent value="channel" className="p-4 space-y-4 m-0">
            <div className="rounded-lg border bg-amber-500/5 border-amber-500/20 p-3">
              <p className="text-xs text-amber-700 dark:text-amber-400">
                ⚠️ The bot must be added as an <b>admin</b> in this channel with "Post messages" permission, otherwise sending will fail silently.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ch-username" className="text-xs flex items-center gap-1.5"><Hash className="w-3 h-3" /> Channel @username (preferred)</Label>
              <Input
                id="ch-username"
                placeholder="@yourchannel"
                value={s.channel_username ?? ""}
                onChange={(e) => setS({ ...s, channel_username: e.target.value })}
              />
              <p className="text-[11px] text-muted-foreground">For public channels — easiest option.</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ch-id" className="text-xs">Channel chat ID (numeric)</Label>
              <Input
                id="ch-id"
                placeholder="-1001234567890"
                value={s.channel_chat_id ?? ""}
                onChange={(e) => setS({ ...s, channel_chat_id: e.target.value })}
              />
              <p className="text-[11px] text-muted-foreground">Required for private channels. Find via @userinfobot.</p>
            </div>
          </TabsContent>

          {/* CONTENT TYPES */}
          <TabsContent value="content" className="p-4 space-y-3 m-0">
            <p className="text-xs text-muted-foreground">Toggle what gets auto-posted each scheduled run.</p>
            {[
              { key: "morning_gif_enabled" as const, icon: ImgIcon, title: "Morning GIF", desc: "Sends a rotating GIF from the library to greet the channel." },
              { key: "quote_image_enabled" as const, icon: Quote, title: "Daily Quote", desc: "Sends a motivational trading quote image generated each day." },
              { key: "holiday_enabled" as const, icon: PartyPopper, title: "Holiday Greeting", desc: "Sends a special message on configured holidays only." },
            ].map(({ key, icon: Icon, title, desc }) => (
              <label key={key} className={`flex items-start justify-between p-3 rounded-lg border cursor-pointer transition-colors ${s[key] ? "bg-primary/5 border-primary/30" : "bg-muted/30"}`}>
                <div className="flex items-start gap-3 flex-1">
                  <div className="p-2 rounded-md bg-primary/10 text-primary">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{title}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{desc}</div>
                  </div>
                </div>
                <Switch checked={s[key]} onCheckedChange={(v) => setS({ ...s, [key]: v })} />
              </label>
            ))}
          </TabsContent>

          {/* GIFS */}
          <TabsContent value="gifs" className="p-4 space-y-3 m-0">
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
              className={`relative rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-all ${dragOver ? "border-primary bg-primary/10 scale-[1.01]" : "border-muted-foreground/30 hover:border-primary/50 hover:bg-muted/40"}`}
            >
              <input
                ref={fileRef}
                type="file"
                accept=".gif,.png,.jpg,.jpeg,.webp,image/*"
                onChange={handleUpload}
                className="hidden"
              />
              {uploading ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-8 h-8 text-primary animate-spin" />
                  <p className="text-sm font-medium">Uploading…</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <div className="p-3 rounded-full bg-primary/10">
                    <Upload className="w-6 h-6 text-primary" />
                  </div>
                  <p className="text-sm font-medium">Drop GIF here or click to browse</p>
                  <p className="text-[11px] text-muted-foreground">.gif .png .jpg .webp · max 20MB</p>
                </div>
              )}
            </div>

            <Input
              placeholder="Optional caption for the next upload"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              className="text-xs"
            />

            {gifs.length === 0 ? (
              <div className="text-xs text-muted-foreground italic p-6 text-center bg-muted/30 rounded-lg">
                <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
                No GIFs yet. Drop one above to start the rotation.
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-[400px] overflow-y-auto pr-1">
                {gifs.map((g) => (
                  <div key={g.id} className={`group relative rounded-lg overflow-hidden border-2 transition-all ${g.is_active ? "border-primary/40" : "border-muted opacity-60"}`}>
                    {g.signedUrl ? (
                      <img
                        src={g.signedUrl}
                        alt={g.caption || "gif"}
                        className="w-full aspect-square object-cover bg-muted"
                        onError={(e) => {
                          const el = e.currentTarget;
                          el.style.display = "none";
                          const fb = el.nextElementSibling as HTMLElement | null;
                          if (fb) fb.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <div
                      className="w-full aspect-square items-center justify-center text-xs text-muted-foreground bg-muted"
                      style={{ display: g.signedUrl ? "none" : "flex" }}
                    >
                      Preview unavailable
                    </div>
                    <div className="absolute top-1.5 left-1.5">
                      {g.is_active ? (
                        <Badge className="text-[9px] px-1.5 h-5 bg-green-500 hover:bg-green-500">Active</Badge>
                      ) : (
                        <Badge variant="secondary" className="text-[9px] px-1.5 h-5">Paused</Badge>
                      )}
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); removeGif(g); }}
                      className="absolute top-1.5 right-1.5 p-1.5 rounded-md bg-destructive/90 text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Delete"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                    <div className="p-2 space-y-1.5 bg-card">
                      {g.caption && <p className="text-[10px] line-clamp-2 text-muted-foreground">{g.caption}</p>}
                      <div className="flex items-center justify-between gap-1">
                        <span className="text-[9px] text-muted-foreground">
                          {g.last_used_at ? `Used ${new Date(g.last_used_at).toLocaleDateString()}` : "Never used"}
                        </span>
                        <button
                          onClick={() => toggleActive(g)}
                          className="text-[10px] px-2 py-0.5 rounded border hover:bg-muted"
                        >
                          {g.is_active ? "Pause" : "Activate"}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* HISTORY */}
          <TabsContent value="history" className="p-4 m-0">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-muted-foreground">Last 20 posts</p>
              <Button size="sm" variant="ghost" onClick={loadLogs} className="h-7 text-xs">Refresh</Button>
            </div>
            {logs.length === 0 ? (
              <div className="text-xs text-muted-foreground italic p-6 text-center bg-muted/30 rounded-lg">
                <History className="w-8 h-8 mx-auto mb-2 opacity-40" />
                No posts logged yet.
              </div>
            ) : (
              <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
                {logs.map((log) => {
                  const meta = POST_TYPE_META[log.post_type] || { icon: Send, label: log.post_type, color: "text-muted-foreground" };
                  const Icon = meta.icon;
                  const ok = log.status === "sent" || log.status === "success";
                  const canRetry = !ok && ["morning_gif", "quote_image", "holiday"].includes(log.post_type);
                  const isRetrying = retryingId === log.id;
                  const errMsg =
                    log.details?.tg?.description ||
                    log.details?.description ||
                    log.details?.message ||
                    log.details?.ai_error ||
                    null;
                  return (
                    <div key={log.id} className="flex items-center gap-3 p-2 rounded-md border bg-card hover:bg-muted/40">
                      <Icon className={`w-4 h-4 ${meta.color}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium truncate">{meta.label}</div>
                        <div className="text-[10px] text-muted-foreground">
                          {log.post_date} · {new Date(log.created_at).toLocaleTimeString()}
                        </div>
                        {!ok && errMsg && (
                          <div className="text-[10px] text-destructive truncate" title={String(errMsg)}>
                            {String(errMsg)}
                          </div>
                        )}
                      </div>
                      {ok ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      ) : (
                        <XCircle className="w-4 h-4 text-destructive" />
                      )}
                      <Badge variant={ok ? "outline" : "destructive"} className="text-[9px] h-5">
                        {log.status}
                      </Badge>
                      {canRetry && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 px-2 text-[10px]"
                          disabled={isRetrying || !isConnected}
                          onClick={() => retryPost(log)}
                          title={isConnected ? "Regenerate and resend this post (may take ~30s)" : "Channel not configured"}
                        >
                          {isRetrying ? (
                            <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Retrying…</>
                          ) : (
                            <><RotateCcw className="w-3 h-3 mr-1" /> Retry</>
                          )}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Footer actions */}
        <div className="flex flex-wrap gap-2 justify-end p-3 border-t bg-muted/30">
          <Button
            variant="default"
            size="sm"
            onClick={() => setManualOpen(true)}
            disabled={!isConnected}
            title="Write your own message and post it now"
          >
            <PenLine className="w-3 h-3 mr-1" />
            Custom post
          </Button>
          <Button variant="outline" size="sm" onClick={() => sendNow("morning_gif")} disabled={sendingNow || !isConnected} title="Send a morning GIF now">
            {sendingType === "morning_gif" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ImgIcon className="w-3 h-3 mr-1" />}
            GIF now
          </Button>
          <Button variant="outline" size="sm" onClick={() => sendNow("quote_image")} disabled={sendingNow || !isConnected} title="Send a quote image now">
            {sendingType === "quote_image" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Quote className="w-3 h-3 mr-1" />}
            Quote now
          </Button>
          <Button variant="outline" size="sm" onClick={() => sendNow("holiday")} disabled={sendingNow || !isConnected} title="Send today's holiday message (if any)">
            {sendingType === "holiday" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <PartyPopper className="w-3 h-3 mr-1" />}
            Holiday now
          </Button>
          <Button variant="outline" size="sm" onClick={() => sendNow()} disabled={sendingNow || !isConnected}>
            {sendingType === "all" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
            Send all
          </Button>
          <Button size="sm" onClick={save} disabled={saving}>
            {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
            Save settings
          </Button>
        </div>
      </CardContent>

      <ManualPostDialog
        open={manualOpen}
        onOpenChange={setManualOpen}
        type="manual_telegram"
        title="Custom Telegram post"
        description="Write your own message. Add an image and/or a link if you want."
        onPosted={loadLogs}
      />
    </Card>
  );
};

export default ChannelSettingsCard;
